/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { z } from 'zod';

// ============================================================================
// Shared Sub-Schemas
// ============================================================================

export const BackgroundImageSetSchema = z.object({
  src: z.string(),
  srcSet: z.string().optional(),
});

export const CallToActionSchema = z.object({
  text: z.string(),
  url: z.string(),
});

// ============================================================================
// Known Web Block Schemas
// ============================================================================

// 1. Product Tiles
export const ProductTileSchema = z.object({
  productId: z.string(),
  iconUrl: z.string(),
  title: z.string(),
  isSearchTab: z.boolean().optional(),
  attractText: z.string().optional(),
  redirectionUrl: z.string(),
});

export const ProductTilesDataSchema = z.object({
  tiles: z
    .array(ProductTileSchema)
    .min(1)
    .refine((tiles) => tiles.some((tile) => tile.productId === 'flights'), {
      message: "Missing required product tile: productId 'flights'",
    }),
  showAll: z
    .object({
      dialogText: z.string().optional(),
      label: z.string().optional(),
      iconUrl: z.string().optional(),
    })
    .optional(),
});

export const ProductTilesBlockSchema = z.object({
  id: z.literal('block_product_tiles'),
  uuid: z.string(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: ProductTilesDataSchema,
});

// 2. Notification Banner
export const NotificationBannerDataSchema = z.object({
  notificationBanner: z
    .object({
      text: z.string().optional(),
      label: z.string().optional(),
      iconUrl: z.string().optional(),
      callToAction: CallToActionSchema.optional(),
    })
    .optional(),
});

export const NotificationBannerBlockSchema = z.object({
  id: z.literal('block_notification_banner'),
  uuid: z.string(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: NotificationBannerDataSchema.optional(),
});

// 3. Hero Banner
export const HeroBannerItemSchema = z.object({
  label: z.string().optional(),
  headline: z.string().optional(),
  subHeadline: z.string().optional(),
  promoCode: z.string().optional(),
  tnc: z.string().optional(),
  backgroundImage: BackgroundImageSetSchema.optional(),
  redirectionUrl: z.string(),
});

export const HeroBannerDataSchema = z.object({
  banners: z.array(HeroBannerItemSchema).optional(),
});

export const HeroBannerBlockSchema = z.object({
  id: z.literal('block_hero_banner'),
  uuid: z.string(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: HeroBannerDataSchema.optional(),
});

// 4. Article Carousel
export const ArticleItemSchema = z.object({
  title: z.string(),
  subTitle: z.string().optional(),
  backgroundImage: BackgroundImageSetSchema.optional(),
  backgroundImageUrl: z.string().optional(),
  redirectionUrl: z.string(),
});

export const ArticleSectionSchema = z.object({
  title: z.string(),
  articles: z.array(ArticleItemSchema),
});

export const ArticleCarouselDataSchema = z.object({
  sections: z.array(ArticleSectionSchema).optional(),
});

export const ArticleCarouselBlockSchema = z.object({
  id: z.literal('block_article_carousel'),
  uuid: z.string(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: ArticleCarouselDataSchema.optional(),
});

// 5. Prime Ads
export const PrimeAdsDataSchema = z.object({
  primeAd: z
    .object({
      adUnitId: z.string().optional(),
      templateId: z.string().optional(),
      adId: z.string().optional(),
    })
    .optional(),
});

export const PrimeAdsBlockSchema = z.object({
  id: z.literal('block_prime_ads'),
  uuid: z.string(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: PrimeAdsDataSchema.optional(),
});

// 6. Cookies Banner
export const CookiesBannerDataSchema = z.object({
  cookiesBanner: z
    .object({
      text: z.string().optional(),
      privacyStatementLink: z.string().optional(),
      privacyStatementText: z.string().optional(),
      closeIconUrl: z.string().optional(),
    })
    .optional(),
});

export const CookiesBannerBlockSchema = z.object({
  id: z.literal('block_cookies_banner'),
  uuid: z.string(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: CookiesBannerDataSchema.optional(),
});

// 7. Footer Ads Banner
export const FooterAdsBannerItemSchema = z.object({
  widgetName: z.string().optional(),
  adUnitId: z.string(),
  templateId: z.string(),
  adId: z.string(),
});

export const FooterAdsBannerDataSchema = z.object({
  banners: z.array(FooterAdsBannerItemSchema).optional(),
});

export const FooterAdsBannerBlockSchema = z.object({
  id: z.literal('block_footer_ads_banner'),
  uuid: z.string(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: FooterAdsBannerDataSchema.optional(),
});

// 8. Bottom Navigation
export const BottomNavigationItemSchema = z.object({
  navigationId: z.string(),
  label: z.string(),
  iconUrl: z.string(),
  redirectionUrl: z.string(),
});

export const BottomNavigationDataSchema = z.object({
  items: z.array(BottomNavigationItemSchema).optional(),
});

export const BottomNavigationBlockSchema = z.object({
  id: z.literal('block_bottom_navigation'),
  uuid: z.string(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: BottomNavigationDataSchema.optional(),
});

// ============================================================================
// Known Web Block IDs & Extensible Generic Block Schema
// ============================================================================

export const KNOWN_WEB_BLOCK_IDS = [
  'block_product_tiles',
  'block_notification_banner',
  'block_hero_banner',
  'block_article_carousel',
  'block_prime_ads',
  'block_cookies_banner',
  'block_footer_ads_banner',
  'block_bottom_navigation',
] as const;

export type KnownWebBlockId = (typeof KNOWN_WEB_BLOCK_IDS)[number];

export const GenericBlockSchema = z.object({
  id: z
    .string()
    .refine((id) => !KNOWN_WEB_BLOCK_IDS.includes(id as KnownWebBlockId), {
      message: 'Known block ID failed strict validation',
    }),
  uuid: z.string().optional(),
  viewport: z.array(z.string()).optional(),
  order: z.number().optional(),
  data: z.record(z.unknown()).optional(),
});

export const KnownWebBlockSchema = z.discriminatedUnion('id', [
  ProductTilesBlockSchema,
  NotificationBannerBlockSchema,
  HeroBannerBlockSchema,
  ArticleCarouselBlockSchema,
  PrimeAdsBlockSchema,
  CookiesBannerBlockSchema,
  FooterAdsBannerBlockSchema,
  BottomNavigationBlockSchema,
]);

export type TKnownWebBlock = z.infer<typeof KnownWebBlockSchema>;
export type TGenericBlock = z.infer<typeof GenericBlockSchema>;
export type THomepageBlock = TKnownWebBlock | TGenericBlock;

// Block Union: Validates known web blocks strictly, allows new/unknown blocks to pass through
export const HomepageBlockSchema = z
  .custom<THomepageBlock>((val) => typeof val === 'object' && val !== null)
  .superRefine((val, ctx) => {
    const block = val as { id?: string };
    const isKnown = KNOWN_WEB_BLOCK_IDS.includes(block.id as KnownWebBlockId);
    const schema = isKnown ? KnownWebBlockSchema : GenericBlockSchema;
    const result = schema.safeParse(val);
    if (!result.success) {
      result.error.issues.forEach((issue) => ctx.addIssue(issue));
    }
  });

// ============================================================================
// Full Homepage API Response Schema
// ============================================================================

export const HomepageConsumerSchema = z.object({
  lob: z.string(),
  feature: z.string(),
});

const HasProductTilesBlockSchema = z.object({
  id: z.literal('block_product_tiles'),
});

export const HomepageDataPayloadSchema = z.object({
  consumer: HomepageConsumerSchema.optional(),
  geoid: z.string().optional(),
  userGeoid: z.string().optional(),
  currency: z.string().optional(),
  locale: z.string().optional(),
  agentType: z.string().optional(),
  blocks: z
    .array(HomepageBlockSchema)
    .refine(
      (blocks) =>
        blocks.some(
          (block) => HasProductTilesBlockSchema.safeParse(block).success,
        ),
      {
        message: 'Missing required block: block_product_tiles',
      },
    ),
  _isFallback: z.boolean().optional(),
});

export const HomepageWrappedApiResponseSchema = z.object({
  success: z.boolean().optional(),
  data: HomepageDataPayloadSchema,
  metadata: z.record(z.unknown()).optional(),
  _isFallback: z.boolean().optional(),
});

export const HomepageApiResponseSchema = z.union([
  HomepageWrappedApiResponseSchema,
  HomepageDataPayloadSchema,
]);

export type THomepageDataPayload = z.infer<typeof HomepageDataPayloadSchema>;
export type THomepageWrappedApiResponse = z.infer<
  typeof HomepageWrappedApiResponseSchema
>;
export type THomepageApiResponse = z.infer<typeof HomepageApiResponseSchema>;
export type TProductTilesBlock = z.infer<typeof ProductTilesBlockSchema>;
export type TNotificationBannerBlock = z.infer<
  typeof NotificationBannerBlockSchema
>;
export type THeroBannerBlock = z.infer<typeof HeroBannerBlockSchema>;
export type TArticleCarouselBlock = z.infer<typeof ArticleCarouselBlockSchema>;
export type TPrimeAdsBlock = z.infer<typeof PrimeAdsBlockSchema>;
export type TCookiesBannerBlock = z.infer<typeof CookiesBannerBlockSchema>;
export type TFooterAdsBannerBlock = z.infer<typeof FooterAdsBannerBlockSchema>;
export type TBottomNavigationBlock = z.infer<
  typeof BottomNavigationBlockSchema
>;
